import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useCart, itemKey } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { resolveImg, money } from '@/lib/api';
import { toast } from 'sonner';

export function CartDrawer() {
  const { items, open, setOpen, updateQty, remove, subtotal, count } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const checkout = () => {
    if (!user) { setOpen(false); toast.error('Please sign in to check out'); navigate('/auth'); return; }
    setOpen(false);
    navigate('/checkout');
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="bg-surface border-border w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl flex items-center gap-2">
            <ShoppingBag size={20} className="text-gold" /> Your Bag ({count})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
            <ShoppingBag size={40} className="mb-4 opacity-40" />
            <p className="text-sm">Your bag is empty.</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-6 space-y-6">
              {items.map((i) => {
                const key = itemKey(i);
                return (
                  <div key={key} data-testid={`cart-item-${i.product.id}`} className="flex gap-4">
                    <img src={resolveImg(i.product.images?.[0])} alt={i.product.name} className="w-20 h-24 object-cover bg-surface-elevated" />
                    <div className="flex-1">
                      <p className="font-serif text-base leading-tight">{i.product.name}</p>
                      {i.size && <span className="text-[11px] font-mono text-muted-foreground">Size {i.size}</span>}
                      {i.product.is_presale && <span className="text-[10px] font-mono uppercase text-gold ml-2">Presale</span>}
                      <p className="font-mono text-sm text-gold mt-1">{money(i.product.price, i.product.currency)}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <button data-testid={`cart-dec-${i.product.id}`} onClick={() => updateQty(key, i.quantity - 1)} className="w-7 h-7 border border-border flex items-center justify-center hover:border-gold"><Minus size={12} /></button>
                        <span className="font-mono text-sm w-5 text-center">{i.quantity}</span>
                        <button data-testid={`cart-inc-${i.product.id}`} onClick={() => updateQty(key, i.quantity + 1)} className="w-7 h-7 border border-border flex items-center justify-center hover:border-gold"><Plus size={12} /></button>
                        <button data-testid={`cart-remove-${i.product.id}`} onClick={() => remove(key)} className="ml-auto text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-border pt-6 space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-mono text-gold text-base">{money(subtotal)}</span>
              </div>
              <p className="text-xs text-muted-foreground">Delivery scheduling & gift options at checkout.</p>
              <button data-testid="cart-drawer-checkout-button" onClick={checkout}
                className="w-full bg-gold text-primary-foreground py-4 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover transition-colors">
                Proceed to Checkout
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
